import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import MuiAppBar from "@mui/material/AppBar";
import { Box, List as MuiList, IconButton, Drawer } from "@mui/material";
import { WinDims } from "../../hooks/Method";
import { CloseRounded, MenuRounded } from "@mui/icons-material";
import "./Dashboard.scss";
import { useLocation } from "react-router-dom";
import { isPosPath } from "../../utils/posPath";

const List = styled(MuiList)({
  "& .MuiListItemButton-root": {
    marginBottom: "0px",
    "&, & .MuiListItemIcon-root": {
      color: "var(--black-color)",
      minWidth: "40px",
    },
  },
  // hover states
  "& .MuiListItemButton-root:hover": {
    backgroundColor: "var(--white-color)",
    "&, & .MuiListItemIcon-root": {
      color: "var(--primaryDark-color)",
    },
    "& .MuiListItemText-root .MuiTypography-root": {
      fontWeight: "500",
      color: "var(--primaryDark-color)",
    },
  },
});

const drawerWidth = 260;

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  top: "inherit",
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

export default function Dashboard(props) {
  const { menu, header, children, showNav = true, ...other } = props;
  const minWidth = 1024;
  const location = useLocation();
  const windowDims = WinDims().width;
  const [inPOS, setInPOS] = useState(false);
  const [open, setOpen] = useState(
    showNav && (windowDims > minWidth ? true : false)
  );
  const toggleDrawer = () => {
    setOpen(!open);
  };

  // `inPOS` only decides whether the hamburger toggle is offered on desktop (see the
  // AppBar below) — the sidebar itself stays open on POS in this template.
  //
  // This used to test `/sell`, a route retired when POS moved to `/pos/*`, so the
  // branch never fired: inPOS was permanently false and the desktop toggle was
  // permanently `display:none`. Harmless while the drawer happened to be open, but
  // if it ever closed there was no way to reopen it without leaving the page. The
  // shared isPosPath() is now the single definition — TopNavShell had its own copy
  // and the two had already drifted, which is what let POS become a dead end there.
  useEffect(() => {
    setInPOS(showNav && isPosPath(location.pathname));
    // `showNav &&` matches the initial state above; without it a shell rendered with
    // showNav={false} would have its sidebar forced open on the first route change.
    setOpen(showNav && windowDims > minWidth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, windowDims]);

  return (
    <div style={{ display: "flex" }} {...other}>
      {/* --- Navbar --- */}
      <AppBar className="appbar">
        <div className="appbar-container">
          <div className="appbar-inner-div">
            <Box
              sx={{
                display: { md: `${inPOS ? "block" : "none"}`, xs: "block" },
              }}
              className="ml-[10px]"
            >
              <IconButton onClick={toggleDrawer} size="small" color="white">
                {open ? <CloseRounded /> : <MenuRounded />}
              </IconButton>
            </Box>
            {(showNav || open) && header}
          </div>
        </div>
      </AppBar>

      {/* --- Menu --- */}
      <Drawer
        open={open}
        onClose={() => setOpen(!open)}
        variant="persistent"
        sx={{
          "& .MuiDrawer-paper": {
            display: open ? "block" : "none",
            width: `${drawerWidth}px`,
            position: { md: "relative", xs: "" },
            backgroundColor: "#e7e5e8",
            border: "none",
            boxShadow: "none",
            borderRadius: "0px",
          },
        }}
      >
        <List
          sx={{
            height: "calc(100vh - 0px)",
            overflow: "auto",
            pt: "70px !important",
            flexGrow: 1,
          }}
        >
          {menu}
        </List>
      </Drawer>

      {/* --- Body --- */}
      <Box
        component="main"
        className="bg-inherit p-[10px] w-[100%] mt-[60px]"
        sx={{
          overflow: "auto",
          height: "calc(100vh - 60px)",
          flexGrow: 1, // Allow it to take available space
        }}
      >
        {children}
      </Box>
    </div>
  );
}
